/* Spreadsheet (CSV) → EmployeeInput rows: parsing + header mapping used by the AI import modal. */
import type { EmployeeInput } from "../../../data/types";
import { LOOKUP } from "../../../i18n";

/** RFC-4180-ish CSV parser (quotes, escaped quotes, CRLF, BOM). Also accepts ; or tab separated text. */
export function parseCsv(text: string): string[][] {
  const s = String(text || "").replace(/^﻿/, "");
  const first = s.split(/\r?\n/, 1)[0] || "";
  const sep = (first.match(/\t/g) || []).length > (first.match(/,/g) || []).length ? "\t" : (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [], cell = "", q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === sep) { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows.map((r) => r.map((x) => x.trim()));
}

export type FieldKey = "nameEn" | "nameAr" | "idNumber" | "nationality" | "gender" | "dept" | "title" | "branch" | "basic" | "housing" | "transport" | "other" | "joined" | "dob" | "mobile" | "email" | "bank" | "iban" | "status";

const ALIASES: [FieldKey, RegExp][] = [
  ["nameAr", /(name.*(ar|arabic))|(arabic.*name)|^الاسم|اسم الموظف/],
  ["nameEn", /^(emp(loyee)?\s*)?(full\s*)?name(\s*\(?en(glish)?\)?)?$|name.*(en|english)|english.*name/],
  ["idNumber", /^(id|iqama|national\s*id|id\s*\/\s*iqama|iqama\s*\/\s*id|id\s*number|iqama\s*(no|number)|رقم الهوية|الهوية|الإقامة|رقم الإقامة)/],
  ["nationality", /^(nat|nationality|country|الجنسية)/],
  ["gender", /^(gender|sex|الجنس)/],
  ["dept", /^(dept|department|القسم|الإدارة)/],
  ["title", /^(job\s*)?(title|position|role|profession)|المسمى|الوظيفة|المهنة/],
  ["branch", /^(branch|location|city|الفرع)/],
  ["basic", /^(basic|sal|salary|basic\s*salary|الراتب الأساسي|الراتب)/],
  ["housing", /^(housing|house|بدل السكن|السكن)/],
  ["transport", /^(transport|transportation|بدل النقل|النقل)/],
  ["other", /^(other|other\s*allowance|بدلات أخرى)/],
  ["joined", /^(doj|joined|join(ing)?\s*date|start(\s*date)?|hire\s*date|date\s*of\s*joining|تاريخ المباشرة|تاريخ التعيين)/],
  ["dob", /^(dob|date\s*of\s*birth|birth\s*date|birthday|تاريخ الميلاد)/],
  ["mobile", /^(mobile|phone|tel|الجوال|الهاتف)/],
  ["email", /^(e-?mail|البريد)/],
  ["iban", /^(iban|bank\s*acc(ount)?|الآيبان|الايبان)/],
  ["bank", /^(bank|bank\s*name|البنك)/],
  ["status", /^(status|الحالة)/],
];

/** Map each header to a field (first match wins; each field used once). */
export function mapHeaders(headers: string[]): (FieldKey | null)[] {
  const used = new Set<FieldKey>();
  return headers.map((h) => {
    const n = h.trim().toLowerCase().replace(/[“”"']/g, "").replace(/\s+/g, " ");
    for (const [f, re] of ALIASES) if (!used.has(f) && re.test(n)) { used.add(f); return f; }
    return null;
  });
}

const numOf = (s: string) => { const v = Number(String(s).replace(/[^\d.-]/g, "")); return Number.isFinite(v) ? v : NaN; };

/** dd/mm/yyyy, d-m-yyyy, yyyy/mm/dd or ISO → ISO (unchanged if unrecognised). */
export function toIsoDate(s: string): string {
  const v = String(s || "").trim();
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(v);
  if (m) return m[1] + "-" + m[2].padStart(2, "0") + "-" + m[3].padStart(2, "0");
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(v);
  if (m) return m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");
  return v;
}

function lookupKey(table: Record<string, [string, string]>, v: string): string {
  const s = v.trim();
  if (!s) return "";
  if (table[s]) return s;
  const up = s.toUpperCase();
  if (table[up]) return up;
  const low = s.toLowerCase();
  for (const [k, [en, ar]] of Object.entries(table)) if (en.toLowerCase() === low || ar === s || k.toLowerCase() === low) return k;
  return s;
}
const NAT_ALIASES: Record<string, string> = { saudi: "SA", "saudi arabia": "SA", indian: "IN", filipino: "PH", philippines: "PH", egyptian: "EG", pakistani: "PK", bangladeshi: "BD", sudanese: "SD", jordanian: "JO", lebanese: "LB", syrian: "SY", yemeni: "YE" };

/** Convert parsed CSV (with header row) to import rows. Unmapped columns are ignored. */
export function rowsToEmployees(table: string[][]): { rows: Partial<EmployeeInput>[]; mapping: (FieldKey | null)[]; headers: string[] } {
  if (!table.length) return { rows: [], mapping: [], headers: [] };
  const [headers, ...body] = table;
  const mapping = mapHeaders(headers);
  const rows = body.map((r) => {
    const o: Record<string, unknown> = {};
    mapping.forEach((f, i) => {
      if (!f) return;
      const v = (r[i] ?? "").trim();
      if (v === "") return;
      switch (f) {
        case "basic": case "housing": case "transport": case "other": o[f] = numOf(v); break;
        case "joined": case "dob": o[f] = toIsoDate(v); break;
        case "nationality": o[f] = NAT_ALIASES[v.toLowerCase()] ?? lookupKey(LOOKUP.NAT, v); break;
        case "dept": o[f] = lookupKey(LOOKUP.DEPT, v); break;
        case "branch": o[f] = lookupKey(LOOKUP.BR, v); break;
        case "gender": o[f] = /^f|^أ|female|انثى|أنثى/i.test(v) ? "F" : "M"; break;
        case "iban": o[f] = v.replace(/\s+/g, "").toUpperCase(); break;
        case "idNumber": o[f] = v.replace(/\D/g, ""); break;
        default: o[f] = v;
      }
    });
    if (!o.gender) o.gender = "M";
    if (!o.status) o.status = "Active";
    return o as Partial<EmployeeInput>;
  });
  return { rows, mapping, headers };
}

/** Sample spreadsheet used by "Try with a sample" (row 3 has a bad IBAN, like the prototype). */
export const SAMPLE_CSV = [
  "Emp Name,الاسم,ID/Iqama,Nationality,Department,Job title,Branch,Sal,DOJ,Bank Acc,Bank,Mobile",
  "Omar Al-Ghamdi,عمر الغامدي,1087654321,Saudi,Sales,Sales Executive,Jeddah,SAR 9500,01/11/2026,SA03 8000 0000 6080 1016 7519,Al Rajhi Bank,+966 55 100 2001",
  "Anil Kumar,أنيل كومار,2345678901,India,Warehouse & Logistics,Storekeeper,Riyadh,4200,15/11/2026,SA44 2000 0001 2345 6789 1234,SNB,+966 55 100 2002",
  "Grace Dela Cruz,غريس ديلا كروز,2456789019,Philippines,Customer Service,Customer Service Agent,Jeddah,5200,01/12/2026,SA12 3456,Riyad Bank,+966 55 100 2003",
].join("\n");

export const FIELD_LABEL: Record<FieldKey, [string, string]> = {
  nameEn: ["Full name (EN)", "الاسم (إنجليزي)"], nameAr: ["Full name (AR)", "الاسم (عربي)"], idNumber: ["ID / Iqama number", "رقم الهوية / الإقامة"],
  nationality: ["Nationality", "الجنسية"], gender: ["Gender", "الجنس"], dept: ["Department", "القسم"], title: ["Job title", "المسمى الوظيفي"],
  branch: ["Branch", "الفرع"], basic: ["Basic salary", "الراتب الأساسي"], housing: ["Housing", "السكن"], transport: ["Transport", "النقل"],
  other: ["Other", "أخرى"], joined: ["Joining date", "تاريخ المباشرة"], dob: ["Date of birth", "تاريخ الميلاد"], mobile: ["Mobile", "الجوال"],
  email: ["Email", "البريد الإلكتروني"], bank: ["Bank", "البنك"], iban: ["IBAN", "الآيبان"], status: ["Status", "الحالة"],
};
